import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Zoom,
  Slide,
  Link as MuiLink
} from '@mui/material';
import {
  Menu as MenuIcon,
  DesignServices,
  Code,
  PhoneAndroid,
  Cloud,
  Security,
  Analytics
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../../components/footer/Footer';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

// Floating animation
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Service data
const services = [
  {
    icon: <DesignServices sx={{ fontSize: 50 }} />,
    title: 'Unified AI Agent',
    description: 'One bot to rule all your messaging platforms. AI-powered conversations across WhatsApp, Messenger, Telegram, and more.',
    color: '#6366f1',
    link: '/unifiedbot'
  },
  {
    icon: <Code sx={{ fontSize: 50 }} />,
    title: 'Web Development',
    description: 'From simple websites to complex web applications, we build responsive and performant solutions.',
    color: '#ec4899',
    link: '/taskorbit'
  },
  {
    icon: <PhoneAndroid sx={{ fontSize: 50 }} />,
    title: 'Mobile Apps',
    description: 'Native and cross-platform mobile applications for iOS and Android with flawless performance.',
    color: '#10b981',
    link: '/unifiedbot'
  },
  {
    icon: <Cloud sx={{ fontSize: 50 }} />,
    title: 'Cloud Solutions',
    description: 'Scalable cloud infrastructure and services to power your business applications.',
    color: '#f59e0b',
    link: '/unifiedbot'
  },
  {
    icon: <Security sx={{ fontSize: 50 }} />,
    title: 'Security',
    description: 'Comprehensive security solutions to protect your data and applications from threats.',
    color: '#ef4444',
    link: '/unifiedbot'
  },
  {
    icon: <Analytics sx={{ fontSize: 50 }} />,
    title: 'Data Analytics',
    description: 'Turn your data into insights with powerful analytics and visualization tools.',
    color: '#8b5cf6',
    link: '/unifiedbot'
  },
];

// Main component
function CustomerExperience() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [visibleServices, setVisibleServices] = useState([]);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Animation effect for services
  useEffect(() => {
    const timer = setTimeout(() => {
      services.forEach((_, index) => {
        setTimeout(() => {
          setVisibleServices(prev => [...prev, index]);
        }, index * 200);
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // Drawer component
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Company Name
      </Typography>
      <List>
        {['Home', 'Services', 'Portfolio', 'About', 'Contact'].map((item) => (
          <ListItem key={item} button>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Navigation Bar */}
        {/* <AppBar position="static" sx={{ backgroundColor: 'background.paper', color: 'text.primary', boxShadow: 'none' }}>
          <Container maxWidth="lg">
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
                Company<span style={{ color: theme.palette.primary.main }}>Name</span>
              </Typography>
              {isMobile ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {['Home', 'Services', 'Portfolio', 'About', 'Contact'].map((item) => (
                    <Button key={item} color="inherit">
                      {item}
                    </Button>
                  ))}
                  <Button variant="contained" sx={{ borderRadius: 2 }}>
                    Get Started
                  </Button>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar> */}

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
          }}
        >
          {drawer}
        </Drawer>

        {/* Hero Section */}
        <Box sx={{ 
          bgcolor: 'background.default', 
          py: 10,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`
        }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Fade in={true} timeout={1000}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  Revolutionize Your Customer Experience
                </Typography>
              </Fade>
              <Slide in={true} direction="up" timeout={800}>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  Deliver seamless, personalized, and measurable CX at every touchpoint.
                </Typography>
              </Slide>
            </Box>
          </Container>
        </Box>

        {/* Services Section */}
        <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in={visibleServices.includes(index)} timeout={800}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 12px 30px -10px ${alpha(service.color, 0.3)}`,
                       // animation: `${float} 2s ease-in-out 1`,
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: 120,
                        background: `linear-gradient(135deg, ${alpha(service.color, 0.1)} 0%, ${alpha(service.color, 0.05)} 100%)`,
                        color: service.color
                      }}
                    >
                      {service.icon}
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="h2" fontWeight="600">
                        {service.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {service.description}
                      </Typography>
                    </CardContent>
                    <Box sx={{ p: 2 }}>
                      <MuiLink component={RouterLink} to={service.link} underline='none'>
                        Learn More
                      </MuiLink>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Footer/>
    </ThemeProvider>
  );
}

export default CustomerExperience;